import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import InstrumentModal from './InstrumentModal';
import MoveInstrumentModal from './MoveInstrumentModal';
import { useAppData } from '../../context/AppContext';

export default function WatchlistTable({ activeList }) {
  const { saveWatchlist } = useAppData();
  const [modalState, setModalState] = useState({ isOpen: false, data: null });
  const [moveState, setMoveState] = useState({ isOpen: false, instrument: null });

  const formatCurrency = (val, currency = 'USD') => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(parseFloat(val) || 0);
    } catch (e) {
      return val;
    }
  };

  if (!activeList) return (
    <div className="flex-1 flex flex-col items-center justify-center text-app-muted bg-app-bg">
      <div className="w-20 h-20 rounded-full bg-app-surface border border-app-border flex items-center justify-center mb-6 opacity-30">
        <FiIcons.FiLayers size={32} />
      </div>
      <p className="text-xs font-black uppercase tracking-[0.3em] text-app-muted/40">Select a terminal watchlist</p>
    </div>
  );

  const saveInstrument = async (instrument) => {
    const exists = activeList.instruments.find(i => i.id === instrument.id);
    const updatedInstruments = exists 
      ? activeList.instruments.map(i => i.id === instrument.id ? instrument : i)
      : [...activeList.instruments, instrument];
    
    await saveWatchlist({ ...activeList, instruments: updatedInstruments });
  };

  const deleteInstrument = async (id) => {
    if (confirm("Remove instrument from this watchlist?")) {
      await saveWatchlist({ ...activeList, instruments: activeList.instruments.filter(i => i.id !== id) });
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-app-bg">
      <div className="p-8 border-b border-app-border flex justify-between items-center bg-app-surface/30 backdrop-blur-xl">
        <div>
          <h2 className="text-3xl font-black text-white uppercase tracking-tighter">{activeList.name}</h2>
          <p className="text-[10px] text-app-accent uppercase font-black tracking-[0.3em] mt-1 opacity-60">
            {activeList.instruments.length} Assets Registered
          </p>
        </div>
        <button 
          onClick={() => setModalState({ isOpen: true, data: null })}
          className="flex items-center gap-3 bg-app-accent text-black px-8 py-4 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-yellow-500 transition-all shadow-2xl shadow-app-accent/20 active:scale-95"
        >
          <FiIcons.FiPlus size={18} /> Add Security
        </button>
      </div>

      <div className="flex-1 overflow-auto p-8">
        <div className="bg-app-surface border border-app-border rounded-2xl overflow-hidden shadow-2xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-app-muted text-[10px] font-black uppercase tracking-[0.3em] bg-app-bg/50 border-b border-app-border">
                <th className="px-8 py-5">Ticker</th>
                <th className="px-8 py-5">Asset Class / Name</th>
                <th className="px-8 py-5">Key Price Levels</th>
                <th className="px-8 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-border/20">
              {activeList.instruments.map(inst => (
                <tr key={inst.id} className="hover:bg-white/[0.015] transition-colors group">
                  <td className="px-8 py-8 font-mono text-app-accent font-black text-xl tracking-tighter">
                    {inst.code}
                    <span className="block text-[9px] font-sans opacity-40 mt-1">{inst.currency || 'USD'}</span>
                  </td>
                  <td className="px-8 py-8">
                    <div className="text-lg font-bold text-app-text">{inst.name}</div>
                    <div className="text-xs text-app-muted truncate max-w-sm mt-2 italic opacity-60">{inst.notes || 'No active thesis'}</div>
                  </td>
                  <td className="px-8 py-8">
                    <div className="flex flex-wrap gap-3">
                      {inst.priceLevels?.map((l, idx) => (
                        <div key={idx} className="flex items-center gap-4 bg-app-bg px-4 py-3 rounded-xl border border-app-border shadow-inner">
                          <span className="text-lg font-black text-white font-mono">
                            {formatCurrency(l.price, inst.currency)}
                          </span>
                          <span className="text-[9px] uppercase font-black text-app-accent bg-app-accent/10 px-2 py-1 rounded border border-app-accent/20">
                            {l.label || l.type}
                          </span>
                        </div>
                      ))}
                      {(!inst.priceLevels || inst.priceLevels.length === 0) && (
                        <span className="text-xs text-app-muted/30 font-mono">NO_LEVELS_SET</span>
                      )}
                    </div>
                  </td>
                  <td className="px-8 py-8 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                      <button 
                        onClick={() => setMoveState({ isOpen: true, instrument: inst })}
                        className="p-3 text-app-muted hover:text-app-accent hover:bg-app-bg rounded-xl transition-all border border-app-border"
                        title="Move to Watchlist"
                      >
                        <FiIcons.FiShare size={18} />
                      </button>
                      <button 
                        onClick={() => setModalState({ isOpen: true, data: inst })}
                        className="p-3 text-app-muted hover:text-white hover:bg-app-bg rounded-xl transition-all border border-app-border"
                        title="Edit Security"
                      >
                        <FiIcons.FiEdit2 size={18} />
                      </button>
                      <button 
                        onClick={() => deleteInstrument(inst.id)}
                        className="p-3 text-app-muted hover:text-app-red hover:bg-app-red/10 rounded-xl transition-all border border-app-border hover:border-app-red/30"
                        title="Delete Security"
                      >
                        <FiIcons.FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {activeList.instruments.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-8 py-20 text-center text-app-muted italic opacity-40 uppercase text-xs tracking-[0.4em]">
                    No securities in this watchlist
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <InstrumentModal 
        isOpen={modalState.isOpen} 
        initialData={modalState.data} 
        onClose={() => setModalState({ isOpen: false, data: null })} 
        onSave={saveInstrument} 
      />

      {moveState.isOpen && (
        <MoveInstrumentModal 
          instrument={moveState.instrument}
          currentWatchlistId={activeList.id}
          onClose={() => setMoveState({ isOpen: false, instrument: null })}
        />
      )}
    </div>
  );
}